import { useLocation, Link } from "react-router-dom";
import { Search, PenTool, DollarSign, Rocket, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface WorkflowStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  paths: string[];
  href: string;
}

const steps: WorkflowStep[] = [
  {
    id: "discover",
    label: "Discover",
    icon: <Search className="w-3.5 h-3.5" />,
    paths: ["/app/keyword-research"],
    href: "/app/keyword-research",
  },
  {
    id: "create",
    label: "Create",
    icon: <PenTool className="w-3.5 h-3.5" />,
    paths: ["/app/nexis-ai", "/app/clip-ai"],
    href: "/app/nexis-ai",
  },
  {
    id: "monetise",
    label: "Monetise",
    icon: <DollarSign className="w-3.5 h-3.5" />,
    paths: ["/app/monetization-ai", "/app/sales-page-builder", "/app/email-list-builder"],
    href: "/app/monetization-ai",
  },
  {
    id: "launch",
    label: "Launch",
    icon: <Rocket className="w-3.5 h-3.5" />,
    paths: ["/app/launch-advisor"],
    href: "/app/launch-advisor",
  },
];

const TOOL_ROUTES = steps.flatMap((s) => s.paths);

export function WorkflowProgressBar() {
  const location = useLocation();
  const { user } = useAuth();
  const [completed, setCompleted] = useState<Set<string>>(new Set());

  const isToolPage = TOOL_ROUTES.some((r) => location.pathname.startsWith(r));
  const currentStepIndex = steps.findIndex((s) =>
    s.paths.some((p) => location.pathname.startsWith(p))
  );

  useEffect(() => {
    if (!user || !isToolPage) return;

    const checkCompleted = async () => {
      const done = new Set<string>();

      const { count: kwCount } = await supabase
        .from("keyword_searches")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((kwCount || 0) > 0) done.add("discover");

      const { count: scriptCount } = await supabase
        .from("nexis_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((scriptCount || 0) > 0) done.add("create");

      const { count: monCount } = await supabase
        .from("monetization_projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      if ((monCount || 0) > 0) done.add("monetise");

      const { count: launchCount } = await supabase
        .from("credit_transactions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("action_type", "launch-advisor");
      if ((launchCount || 0) > 0) done.add("launch");

      setCompleted(done);
    };

    checkCompleted();
  }, [user, isToolPage]);

  if (!isToolPage) return null;

  return (
    <div className="px-6 py-3 border-b border-border/50 bg-muted/30">
      <div className="max-w-4xl mx-auto flex items-center gap-1 sm:gap-2">
        {steps.map((step, index) => {
          const isCurrent = index === currentStepIndex;
          const isDone = completed.has(step.id);
          const isPast = index < currentStepIndex;

          return (
            <div key={step.id} className="flex items-center flex-1 min-w-0">
              <Link
                to={step.href}
                className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  isCurrent
                    ? "bg-primary text-primary-foreground"
                    : isDone
                      ? "bg-green-500/15 text-green-400 hover:bg-green-500/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {isDone && !isCurrent ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  step.icon
                )}
                <span className="hidden sm:inline">{step.label}</span>
              </Link>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-px mx-1 ${isPast || isDone ? "bg-green-500/40" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
